// ── #2762 — Delivery-tier workbasket ────────────────────────────────────────
//
// A NEW, dedicated page (not an extension of the Dashboard's 5-item
// "My assigned work" card) unifying issues + Open Questions + Risks + NFRs
// assigned to the caller, across every accessible project. Built against two
// confirmed recons: no naive status union works (four independent status
// vocabularies), and "act on this" is not one shape — this page therefore
// does NOT try to render a generic action UI. Each row links out to that
// row's own existing, real page (RisksPage / NfrsPage / OpenQuestionsPage /
// the project dashboard for issues), where the real, already-validated
// accept-disposition/status/edit UI already lives. No new write path.
//
// Consumes GET /dashboard/workbasket (#2762 — live). Internal/Delivery-tier
// only: the endpoint 403s a CLIENT-only caller outright (the Business-tier
// equivalent is the client portal's own assigned-items view, #2761), so a
// 403 here bounces to /projects — same convention Dashboard.tsx uses for its
// own "no accessible tenants" case.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Sidebar from '../components/Sidebar';
import { API_BASE } from '../lib/api';
import { TimingBadge } from '../components/StoryStatusCard';
import type { AssignedWorkItem, WorkbasketSourceType } from '../types/dashboard';
import { isWaitingOnBusinessDecision, WAITING_ON_BUSINESS_DECISION_LABEL } from '../lib/nfrStatus';

const SOURCE_LABEL: Record<WorkbasketSourceType, string> = {
  ISSUE: 'Issue',
  OPEN_QUESTION: 'Open Question',
  RISK: 'Risk',
  NFR: 'NFR',
};

// #3167 — the type filter's options, in display order. 'ALL' is the
// no-filter value; the rest are the WorkbasketSourceType values themselves,
// so the URL reads ?type=RISK etc.
const TYPE_FILTER_OPTIONS: Array<{ value: 'ALL' | WorkbasketSourceType; label: string }> = [
  { value: 'ALL', label: 'All types' },
  { value: 'ISSUE', label: 'Issues' },
  { value: 'OPEN_QUESTION', label: 'Open Questions' },
  { value: 'RISK', label: 'Risks' },
  { value: 'NFR', label: 'NFRs' },
];
const SOURCE_TYPES = new Set<string>(['ISSUE', 'OPEN_QUESTION', 'RISK', 'NFR']);

/**
 * #3167 — client-side filtering. GET /dashboard/workbasket returns every
 * assigned OQ, Risk and NFR (only Issues are capped, and `truncated` already
 * says so), so filtering the loaded list is complete; no API change needed.
 * The two filters combine with AND. 'ALL' means "don't filter on this".
 */
export function applyWorkbasketFilters(
  items: AssignedWorkItem[],
  type: 'ALL' | WorkbasketSourceType,
  projectId: string,
): AssignedWorkItem[] {
  return items.filter(
    (i) => (type === 'ALL' || i.sourceType === type) && (projectId === 'ALL' || i.projectId === projectId),
  );
}

// Each sourceType routes to its OWN real page — never a shared generic form.
// Reading and acting are different permission questions (#2748's own
// precedent), so this deliberately links to the existing list page rather
// than a specific row: the destination page's own real action UI (the
// accept-disposition modal, the issue edit form) is where the user acts.
function destinationFor(item: AssignedWorkItem): string {
  switch (item.sourceType) {
    case 'OPEN_QUESTION':
      return `/projects/${item.projectId}/open-questions`;
    case 'RISK':
      return `/projects/${item.projectId}/risks`;
    case 'NFR':
      return `/projects/${item.projectId}/nfrs`;
    case 'ISSUE':
    default:
      // Matches DashboardZone2Action's MyAssignedWorkCard convention exactly
      // — there is no dedicated per-issue route, only the project dashboard.
      return `/projects/${item.projectId}`;
  }
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text)',
  textDecoration: 'none',
  fontSize: 14,
};

const typeTagStyle: React.CSSProperties = {
  display: 'inline-block',
  fontSize: 9,
  fontWeight: 700,
  letterSpacing: 0.3,
  padding: '1px 6px',
  marginRight: 10,
  borderRadius: 4,
  background: 'var(--surface-alt)',
  color: 'var(--text-secondary)',
  verticalAlign: 'middle',
};

const waitingTagStyle: React.CSSProperties = {
  marginLeft: 8,
  padding: '1px 8px',
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
  background: 'rgba(99,102,241,0.12)',
  color: 'var(--primary)',
  flexShrink: 0,
  whiteSpace: 'nowrap',
};

const filterLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--text-secondary)',
};

const selectStyle: React.CSSProperties = {
  padding: '6px 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 13,
  fontWeight: 400,
  maxWidth: 280,
};

const clearButtonStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: 13,
  cursor: 'pointer',
};

export default function WorkbasketPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  // R1 finding — same monotonic-request-id pattern as Dashboard.tsx: two
  // overlapping load() calls (a double-click on "Try again" before the first
  // settles, or `token` changing mid-flight on a partner-context switch) must
  // not let the request that resolves LAST win over the one that resolves
  // FIRST if they're the wrong order. isStale() checks both unmount and
  // "a newer load() has since started."
  const requestSeq = useRef(0);

  const [items, setItems] = useState<AssignedWorkItem[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // #3167 — filter state lives in the URL (?type=…&project=…) so it survives
  // refresh and Back, and a filtered view can be shared. An unknown type, or a
  // project the caller has no items in (stale link, item since resolved),
  // falls back to 'ALL' rather than showing a confusing empty page.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawType = searchParams.get('type');
  const typeFilter: 'ALL' | WorkbasketSourceType =
    rawType && SOURCE_TYPES.has(rawType) ? (rawType as WorkbasketSourceType) : 'ALL';
  const rawProject = searchParams.get('project');
  const projectFilter =
    rawProject && (items ?? []).some((i) => i.projectId === rawProject) ? rawProject : 'ALL';

  const setFilter = useCallback((key: 'type' | 'project', value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value === 'ALL') next.delete(key);
      else next.set(key, value);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('type');
      next.delete('project');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Project options: every project the caller has at least one item in (no
  // dead options), sorted by name. Each count follows the OTHER filter (type),
  // and each type option's count follows the project filter, so a count always
  // equals what you'd see by picking that option.
  const projectOptions = useMemo(() => {
    const byId = new Map<string, { id: string; name: string; count: number }>();
    for (const i of items ?? []) {
      const entry = byId.get(i.projectId) ?? { id: i.projectId, name: i.projectName, count: 0 };
      if (typeFilter === 'ALL' || i.sourceType === typeFilter) entry.count += 1;
      byId.set(i.projectId, entry);
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [items, typeFilter]);

  const typeCounts = useMemo(() => {
    const counts: Record<string, number> = { ALL: 0 };
    for (const i of items ?? []) {
      if (projectFilter !== 'ALL' && i.projectId !== projectFilter) continue;
      counts.ALL += 1;
      counts[i.sourceType] = (counts[i.sourceType] ?? 0) + 1;
    }
    return counts;
  }, [items, projectFilter]);

  const visibleItems = useMemo(
    () => applyWorkbasketFilters(items ?? [], typeFilter, projectFilter),
    [items, typeFilter, projectFilter],
  );
  const filtersActive = typeFilter !== 'ALL' || projectFilter !== 'ALL';

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const isStale = (): boolean => !mountedRef.current || seq !== requestSeq.current;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/workbasket`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (isStale()) return;

      // Not this endpoint's audience (CLIENT-tier, or no accessible tenants
      // at all) — bounce to /projects, same convention Dashboard.tsx uses.
      if (res.status === 403) {
        navigate('/projects');
        return;
      }
      if (!res.ok) throw new Error('Failed to load workbasket');

      const data = await res.json();
      if (isStale()) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTruncated(data?.truncated === true);
      setError(null);
    } catch (err) {
      if (isStale()) return;
      setError(err instanceof Error ? err.message : 'Failed to load workbasket');
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32 }} data-testid="workbasket-page">
        <header style={{ marginBottom: 24 }}>
          {/* In the header so it's there in every state: list, empty, no-match, error. */}
          <Link
            to="/projects"
            data-testid="workbasket-back-to-projects"
            style={{ display: 'inline-block', marginBottom: 8, fontSize: 13, color: 'var(--primary)', textDecoration: 'none' }}
          >
            &larr; Back to Projects
          </Link>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0, fontFamily: 'var(--font-display)' }}>
            Workbasket
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 }}>
            Everything currently assigned to you across your projects.
          </p>
        </header>

        {truncated && !error && (
          <div
            data-testid="workbasket-truncated-notice"
            role="status"
            style={{
              marginBottom: 16,
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--warning)',
              background: 'var(--surface-alt)',
              color: 'var(--text-secondary)',
              fontSize: 13,
            }}
          >
            You have more assigned issues than fit here — showing the most urgent ones first.
          </div>
        )}

        {!error && (items ?? []).length > 0 && (
          <div
            data-testid="workbasket-filters"
            style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}
          >
            <label style={filterLabelStyle}>
              Type
              <select
                data-testid="workbasket-filter-type"
                value={typeFilter}
                onChange={(e) => setFilter('type', e.target.value)}
                style={selectStyle}
              >
                {TYPE_FILTER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} ({typeCounts[o.value] ?? 0})
                  </option>
                ))}
              </select>
            </label>
            <label style={filterLabelStyle}>
              Project
              <select
                data-testid="workbasket-filter-project"
                value={projectFilter}
                onChange={(e) => setFilter('project', e.target.value)}
                style={selectStyle}
              >
                <option value="ALL">All projects</option>
                {projectOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.count})
                  </option>
                ))}
              </select>
            </label>
            <span data-testid="workbasket-filter-summary" role="status" aria-live="polite" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              Showing {visibleItems.length} of {(items ?? []).length}
            </span>
            {filtersActive && (
              <button
                type="button"
                data-testid="workbasket-clear-filters"
                onClick={clearFilters}
                style={clearButtonStyle}
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {error ? (
          <div data-testid="workbasket-error" style={{ textAlign: 'center', padding: '64px 0' }}>
            <p style={{ fontSize: 16, color: 'var(--text)', marginBottom: 8 }}>We couldn&rsquo;t load your workbasket.</p>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>{error}</p>
            <button
              type="button"
              onClick={() => void load()}
              style={{
                padding: '8px 20px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--primary)',
                color: '#fff',
                border: 'none',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Try again
            </button>
          </div>
        ) : loading && items === null ? (
          <div data-testid="workbasket-loading" style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Loading&hellip;
          </div>
        ) : (items ?? []).length === 0 ? (
          <div
            data-testid="workbasket-empty"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              borderRadius: 'var(--radius-lg)',
              border: '1px dashed var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>You&rsquo;re all caught up</div>
            <div style={{ fontSize: 13 }}>Nothing assigned to you right now.</div>
          </div>
        ) : visibleItems.length === 0 ? (
          <div
            data-testid="workbasket-no-match"
            style={{
              padding: '48px 24px',
              textAlign: 'center',
              borderRadius: 'var(--radius-lg)',
              border: '1px dashed var(--border)',
              background: 'var(--surface)',
              color: 'var(--text-secondary)',
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Nothing matches these filters</div>
            <div style={{ fontSize: 13, marginBottom: 16 }}>Try another type or project, or clear the filters.</div>
            <button type="button" onClick={clearFilters} style={clearButtonStyle}>
              Clear filters
            </button>
          </div>
        ) : (
          <div
            data-testid="workbasket-list"
            style={{
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              background: 'var(--surface)',
              boxShadow: 'var(--shadow-sm)',
              overflow: 'hidden',
            }}
          >
            {visibleItems.map((item) => (
              <Link
                key={`${item.sourceType}-${item.id}`}
                to={destinationFor(item)}
                style={rowStyle}
                data-testid="workbasket-row"
                data-source-type={item.sourceType}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                  <span data-testid="workbasket-row-type" style={typeTagStyle}>{SOURCE_LABEL[item.sourceType]}</span>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</span>
                  <span style={{ color: 'var(--text-secondary)', marginLeft: 8 }}>· {item.projectName}</span>
                  {/* SL.L4a (#2960, D15) — the sprint the issue's backlog item is in. */}
                  {item.sprint && (
                    <span data-testid="workbasket-sprint-tag" title={item.sprint.name} style={{ color: 'var(--text-secondary)', marginLeft: 8, flexShrink: 0 }}>
                      Sprint {item.sprint.number}
                    </span>
                  )}
                  {/* #2834 — the owner can't act on a NEEDS_BUSINESS_DECISION NFR
                      (NfrsPage only acts on GAP), so say what it's waiting on. */}
                  {isWaitingOnBusinessDecision(item) && (
                    <span data-testid="workbasket-waiting-on-business-decision" style={waitingTagStyle}>
                      {WAITING_ON_BUSINESS_DECISION_LABEL}
                    </span>
                  )}
                </span>
                {item.timing ? (
                  <TimingBadge timing={item.timing} daysLate={item.daysLate ?? 0} />
                ) : (
                  <span style={{ color: 'var(--text-secondary)', fontSize: 12, flexShrink: 0 }}>View &rarr;</span>
                )}
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
